/**
 * Real, on-demand per-pay-run audit-trail resolver (C10, #22332). Self-
 * fetching hook, NOT a `dev_*.ts`/`useDevBackendData` boot-time seed — same
 * "why" as `use_my_pay.ts`/`use_session_facts.ts`: this is real production
 * OAuth-gated work, live in every environment including production, and the
 * per-run fetch only makes sense triggered by the drawer actually opening
 * for a SPECIFIC run, not at app boot for every run that exists.
 *
 * On-demand, not boot-time eager: `payRunId` is `null` whenever the drawer
 * is closed (or open for no run), in which case this hook makes NO network
 * call at all — it only fetches once a caller passes a real pay-run id
 * (`PayrollHistoryScreen` passes the currently-open run's id, `null`
 * otherwise). This replaces the deleted `dev_pay_run_audit.ts`'s boot-time
 * N+1 fan-out (`main.tsx` used to resolve every pay run's audit trail
 * up front, whether or not its drawer was ever opened).
 *
 * Reuses the SAME shared `useOAuthEntityContext()` (`lib/auth/
 * oauth_entity_context.ts`, C1 #22323) every other real hook in this epic
 * consumes — never re-resolves its own entity context. See
 * `wagepro_store_pay_run_audit.ts`'s module docblock for why the real fetch
 * goes through `audit.list` (entity-wide, paginated, filtered client-side)
 * rather than `audit.listByDocument` (not reachable by a WagePro OAuth
 * token — a deliberate, adversarially-reviewed security decision, not an
 * oversight).
 */
import * as React from 'react';
import { getTrpcClient } from '../lib/trpc.js';
import { useOAuthEntityContext } from '../lib/auth/oauth_entity_context.js';
import {
  approverDisplayNameFromRows,
  classifyAuditListFailure,
  fetchPayRunAuditRows,
  PAY_RUN_DOCUMENT_TYPE,
  resolvePayRunAuditState,
  type PayRunAuditLoadFailure,
  type PayRunAuditState,
  type RealAuditEntryRow,
} from './wagepro_store_pay_run_audit.js';

export interface UsePayRunAuditResult {
  state: PayRunAuditState;
  /**
   * The approver's display name from this run's `payroll.pay_run.approve`
   * audit row, once resolved — `undefined` while loading, on failure, or
   * when no such row (or no name) exists. See `approverDisplayNameFromRows`.
   */
  approver_display_name: string | undefined;
  /** Re-runs the fetch from scratch for the CURRENT `payRunId`. Only meaningful for the `error` state. */
  retry: () => void;
}

/**
 * The signed-in admin's real audit trail for one specific pay run, fetched
 * only when `payRunId` is non-null (the drawer is open for that run).
 * `payRunCreatedAt` MUST be that same run's own `PayRun.created_at` (`null`
 * only when `payRunId` is also `null`) — it anchors
 * `fetchPayRunAuditRows`'s `created_at` early-exit optimization (see that
 * function's doc comment, `wagepro_store_pay_run_audit.ts`).
 *
 * Switching `payRunId` (a different run's drawer opens, or the drawer
 * closes) synchronously clears any previous run's `rows`/`failure`/
 * `truncated` in the SAME render that changes `payRunId` — same MOTIVATION
 * as `use_my_pay.ts`'s `select_employer` fix (PR #22237 adversarial
 * finding), though a different mechanism: `select_employer` clears state
 * from an event handler (always safe); this hook has no event handler to
 * hook into (`payRunId` is a prop, not a local action), so it uses React's
 * documented in-render "adjusting state when a prop changes" idiom instead
 * — see the `previousPayRunId` comment below. Without this, a `useEffect`
 * re-fetch (which only runs AFTER React commits and paints) would leave one
 * committed render showing the PREVIOUS run's audit
 * entries under the NEW run's drawer title.
 */
export function usePayRunAudit(
  payRunId: string | null,
  payRunCreatedAt: string | null,
): UsePayRunAuditResult {
  const entityContext = useOAuthEntityContext();
  const [rows, setRows] = React.useState<RealAuditEntryRow[] | undefined>(
    undefined,
  );
  const [failure, setFailure] = React.useState<
    PayRunAuditLoadFailure | undefined
  >(undefined);
  const [truncated, setTruncated] = React.useState(false);
  const [reloadToken, setReloadToken] = React.useState(0);

  // Synchronous clear on `payRunId` change (see doc comment above) — React's
  // own documented "adjusting state when a prop changes" pattern
  // (https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes):
  // a `useState`-tracked "previous id", compared and corrected DURING render
  // via a conditional `setState` call. This is NOT the same as mutating a
  // `useRef` during render (adversarial review finding on an earlier version
  // of this hook) — a ref write during render is an unguarded side effect
  // that can silently double-apply (or, under a discarded/interrupted
  // render, silently NOT apply) since nothing about it is transactional with
  // the render itself; React explicitly recognizes and special-cases THIS
  // conditional-`useState`-in-render idiom (it immediately re-renders before
  // painting, discarding the stale render, exactly like `key`-based reset)
  // so it is safe under Strict Mode's dev-mode double-invoke and under
  // concurrent rendering.
  const [previousPayRunId, setPreviousPayRunId] = React.useState(payRunId);
  if (previousPayRunId !== payRunId) {
    setPreviousPayRunId(payRunId);
    setRows(undefined);
    setFailure(undefined);
    setTruncated(false);
  }

  React.useEffect(() => {
    if (payRunId === null || payRunCreatedAt === null) return;
    if (entityContext.status !== 'ready') return;
    // Captured as its own consts so the nested async closure below
    // references definitely-string values rather than relying on narrowing
    // of `entityContext`/`payRunId` surviving across the closure boundary —
    // same pattern `use_my_pay.ts`/`use_session_facts.ts` use.
    const firmId: string = entityContext.firm_id;
    const entityId: string = entityContext.entity_id;
    const runId: string = payRunId;
    const runCreatedAt: string = payRunCreatedAt;
    let cancelled = false;
    setRows(undefined);
    setFailure(undefined);
    setTruncated(false);

    async function load(): Promise<void> {
      try {
        const client = getTrpcClient();
        const { rows: fetchedRows, truncated: fetchedTruncated } =
          await fetchPayRunAuditRows(
            {
              listAudit: (input) => client.audit.list.query(input),
            },
            { firm_id: firmId, entity_id: entityId },
            PAY_RUN_DOCUMENT_TYPE,
            runId,
            runCreatedAt,
          );
        if (cancelled) return;
        setRows(fetchedRows);
        setTruncated(fetchedTruncated);
      } catch (err) {
        if (cancelled) return;
        console.error(
          '[wagepro-web] Failed to resolve the real pay-run audit trail:',
          err,
        );
        setFailure(classifyAuditListFailure(err));
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [payRunId, payRunCreatedAt, entityContext, reloadToken]);

  const state = resolvePayRunAuditState({
    entityContextStatus: entityContext.status,
    payRunId,
    rows,
    failure,
    truncated,
  });

  return {
    state,
    approver_display_name: approverDisplayNameFromRows(rows),
    retry: () => {
      setReloadToken((token) => token + 1);
    },
  };
}
